<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Kampanyalar"
      title="Network Offer'lar"
      description="Network tarafındaki offer kayıtlarını senkronize edin ve panelde görünen alanları offer tablosu üzerinden yönetin."
    />

    <v-card
      class="admin-table-card"
      outlined
      style="overflow: hidden;"
    >
      <v-alert
        v-if="tableError"
        dense
        outlined
        type="error"
        class="ma-4 mb-0"
      >
        {{ tableError }}
      </v-alert>

      <v-tabs
        v-model="activeTab"
        class="admin-tabs"
        @change="onTabChange"
      >
        <v-tab
          v-for="source in companySources"
          :key="source.id"
        >
          {{ source.sourceType.displayName }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item
          v-for="source in companySources"
          :key="source.id"
        >
          <v-card flat>
            <v-toolbar
              flat
              class="admin-page-toolbar"
            >
              <v-toolbar-title>{{ currentSourceLabel }}</v-toolbar-title>
              <v-chip
                small
                outlined
                color="primary"
              >
                {{ totalElements }} offer
              </v-chip>
              <v-chip
                v-if="lastCatalogUpdateLabel"
                small
                outlined
                color="info"
                class="sync-fetch-chip"
              >
                <span>Son network fetch:</span>
                <strong class="ml-1">{{ lastCatalogUpdateLabel }}</strong>
                <span
                  v-if="lastCatalogUpdateSourceLabel"
                  class="ml-1"
                >
                  · {{ lastCatalogUpdateSourceLabel }}
                </span>
              </v-chip>
              <v-select
                v-model="selectedStatusFilter"
                :items="offerStatusFilterOptions"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
                label="Offer Durumu"
                class="status-filter mr-3"
                @change="onStatusFilterChange"
              ></v-select>
              <v-text-field
                v-model="searchValue"
                append-icon="mdi-magnify"
                label="Network Offer Ara"
                placeholder="Network offer'ları adı ile ara..."
                single-line
                hide-details
                outlined
                dense
                class="search-field mr-3"
                clearable
                @input="onSearch"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                outlined
                small
                :loading="fetchLoading"
                @click="fetchCampaigns()"
              >
                <v-icon left>
                  {{ icons.mdiSync }}
                </v-icon>
                Networklerden Çek
              </v-btn>
            </v-toolbar>

            <v-data-table
              dense
              :loading="loading"
              loading-text="Yükleniyor"
              :headers="headers"
              :items="campaigns"
              :options.sync="options"
              :server-items-length="totalElements"
              :footer-props="{
                itemsPerPageOptions: [10, 20, 50, 100],
              }"
              class="admin-data-table elevation-0"
              @update:options="onUpdateTableOptions"
            >
              <template v-slot:item.logo="{ item }">
                <v-avatar
                  size="40"
                  class="my-2"
                >
                  <v-img
                    v-if="item.logo"
                    :src="item.logo"
                    contain
                  ></v-img>
                  <span v-else>-</span>
                </v-avatar>
              </template>

              <template v-slot:item.background="{ item }">
                <v-avatar
                  size="40"
                  class="my-2"
                  tile
                >
                  <v-img
                    v-if="item.background"
                    :src="item.background"
                    contain
                  ></v-img>
                  <span v-else>-</span>
                </v-avatar>
              </template>

              <template v-slot:item.offerStatus="{ item }">
                <v-chip
                  small
                  :color="getOfferStatusColor(item.offerStatus)"
                  outlined
                >
                  {{ getOfferStatusText(item.offerStatus) }}
                </v-chip>
              </template>

              <template v-slot:item.payout="{ item }">
                <div class="d-flex align-center payout-cell">
                  <span>{{ formatPayout(item) }}</span>
                </div>
              </template>

              <template v-slot:item.offerId="{ item }">
                {{ item.offerId || '-' }}
              </template>

              <template v-slot:item.networkSource="{ item }">
                {{ item.networkSource || '-' }}
              </template>

              <template v-slot:item.modifiedAt="{ item }">
                <span>{{ formatDateTime(item.modifiedAt) }}</span>
              </template>

              <template v-slot:item.name="{ item }">
                <div class="offer-row">
                  <strong>{{ item.name || '-' }}</strong>
                  <span>{{ item.offerRefId ? `Ref #${item.offerRefId}` : 'Offer kaydı' }}</span>
                </div>
              </template>

              <template v-slot:item.homepageUrl="{ item }">
                <div
                  v-if="item.homepageUrl"
                  class="text-truncate"
                  style="max-width: 250px;"
                  :title="item.homepageUrl"
                >
                  {{ item.homepageUrl }}
                </div>
                <span v-else>-</span>
              </template>

              <template v-slot:item.action="{ item }">
                <v-btn
                  icon
                  small
                  aria-label="Markayı düzenle"
                  title="Markayı düzenle"
                  @click="editItem(item)"
                >
                  <v-icon
                    small
                    color="info"
                  >
                    {{ icons.mdiPencil }}
                  </v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>

      <v-dialog
        :value="editDialog"
        :persistent="editSaveLoading || aboutImageUploading"
        max-width="1120px"
        content-class="offer-editor-dialog"
        @input="onEditDialogInput"
      >
        <v-card class="admin-dialog-card">
          <v-toolbar
            dense
            :elevation="0"
          >
            <v-toolbar-title>Markayı düzenle</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              aria-label="Marka editörünü kapat"
              title="Kapat"
              :disabled="editSaveLoading || aboutImageUploading"
              @click="closeEdit"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-progress-linear
            v-if="editLoadLoading"
            indeterminate
            aria-label="Offer bilgileri yükleniyor"
          ></v-progress-linear>
          <v-card-text>
            <v-alert
              v-if="editLoadError"
              type="error"
              outlined
              class="mt-4"
            >
              {{ editLoadError }}
              <v-btn
                text
                small
                @click="editItem(editedItem)"
              >
                Tekrar dene
              </v-btn>
            </v-alert>
            <v-container
              v-if="!editLoadLoading && !editLoadError"
              class="offer-composer"
            >
              <div class="offer-settings">
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="editedItem.name"
                      hide-details
                      :disabled="editSaveLoading"
                      outlined
                      dense
                      label="Offer Adı"
                      placeholder="Offer Adı"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model="editedItem.offerId"
                      hide-details
                      outlined
                      dense
                      label="Offer ID"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model="editedItem.networkSource"
                      hide-details
                      outlined
                      dense
                      label="Network"
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-select
                      v-model="editedItem.active"
                      hide-details
                      :disabled="editSaveLoading"
                      :items="offerStatusOptions"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                      label="Offer Durumu"
                    ></v-select>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    class="d-flex align-center"
                  >
                    <v-switch
                      v-model="editedItem.popular"
                      hide-details
                      dense
                      :disabled="editSaveLoading"
                      inset
                      label="Öne çıkan offer"
                    ></v-switch>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model.number="editedItem.settlementHoldDays"
                      :disabled="editSaveLoading"
                      outlined
                      dense
                      type="number"
                      min="1"
                      max="365"
                      label="Kesinleşme (gün)"
                      hint="Onaylanan satışların bekleme süresi."
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      v-model.number="editedItem.reconciliationWindowDays"
                      :disabled="editSaveLoading"
                      outlined
                      dense
                      type="number"
                      min="1"
                      max="365"
                      label="Mutabakat (gün)"
                      hint="Network takibi; sorguya 7 gün tampon eklenir."
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>
              <v-row class="offer-about-section">
                <v-col cols="12">
                  <label
                    id="offer-about-label"
                    class="subtitle-1 font-weight-medium"
                  >
                    Marka Hakkında
                  </label>
                  <quill-editor
                    v-if="aboutHtmlAvailable"
                    :key="editRequestId"
                    ref="offerAboutEditor"
                    v-model="editedItem.aboutHtml"
                    class="offer-about-editor"
                    aria-labelledby="offer-about-label"
                    :options="aboutEditorOptions"
                    :disabled="editSaveLoading || aboutImageUploading"
                  />
                  <v-alert
                    v-else
                    dense
                    outlined
                    type="info"
                    class="mb-0"
                  >
                    Marka Hakkında alanı bu sunucu sürümünde kullanılamıyor. Diğer alanları güncelleyebilirsiniz.
                  </v-alert>
                  <p
                    v-if="aboutImageUploading"
                    class="text-caption mt-2"
                    role="status"
                  >
                    Görsel yükleniyor…
                  </p>
                  <div class="text-caption mt-4">
                    {{ editedItem.networkSource }} · Network kimliği: {{ editedItem.offerId || '-' }}
                    · Bağlı kampanya: {{ editedItem.campaignCount == null ? '-' : editedItem.campaignCount }}
                  </div>
                  <details class="mt-4">
                    <summary>Kayıtlı değerler ve değişiklikler</summary>
                    <v-simple-table
                      dense
                      class="mt-2"
                    >
                      <thead><tr><th>Alan</th><th>Kayıtlı</th><th>Düzenlenen</th></tr></thead>
                      <tbody>
                        <tr><th>Ad</th><td>{{ editOriginal.name }}</td><td>{{ editedItem.name }}</td></tr>
                        <tr><th>Durum</th><td>{{ getOfferStatusText(editOriginal.active) }}</td><td>{{ getOfferStatusText(editedItem.active) }}</td></tr>
                        <tr><th>Öne çıkan</th><td>{{ editOriginal.popular ? 'Evet' : 'Hayır' }}</td><td>{{ editedItem.popular ? 'Evet' : 'Hayır' }}</td></tr>
                        <tr><th>Kesinleşme günü</th><td>{{ editOriginal.settlementHoldDays }}</td><td>{{ editedItem.settlementHoldDays }}</td></tr>
                        <tr><th>Mutabakat günü</th><td>{{ editOriginal.reconciliationWindowDays }}</td><td>{{ editedItem.reconciliationWindowDays }}</td></tr>
                      </tbody>
                    </v-simple-table>
                  </details>
                  <v-alert
                    v-if="offerHasCascadeImpact"
                    dense
                    outlined
                    type="warning"
                    class="mt-4"
                  >
                    <div v-if="offerBecomingInvisible">
                      Markayı kapatmak bağlı kampanya ve linkleri tamamlar; etkilenen kullanıcılara bildirim oluşturabilir.
                    </div>
                    <div v-if="editedItem.popular !== editOriginal.popular">
                      Öne çıkan değişikliği bağlı kampanyalara da uygulanır.
                    </div>
                    <v-checkbox
                      v-model="offerImpactAcknowledged"
                      :disabled="editSaveLoading"
                      label="Bağlı kayıtlara etkisini onaylıyorum"
                      hide-details
                      dense
                    ></v-checkbox>
                  </v-alert>
                </v-col>
              </v-row>
              <v-alert
                v-if="editError"
                dense
                outlined
                type="error"
                class="mb-4"
              >
                {{ editError }}
              </v-alert>
              <v-row>
                <v-col>
                  <div class="d-flex flex-column align-center">
                    <v-img
                      v-if="logoPreview || editedItem.logo"
                      :src="logoPreview || editedItem.logo"
                      max-width="200"
                      max-height="80"
                      contain
                      class="mb-4"
                    ></v-img>
                    <input
                      ref="logoInput"
                      type="file"
                      accept="image/*"
                      style="display: none"
                      @change="onLogoSelected"
                    />
                    <v-btn
                      color="primary"
                      outlined
                      :disabled="editSaveLoading"
                      @click="$refs.logoInput.click()"
                    >
                      <v-icon left>
                        {{ icons.mdiUpload }}
                      </v-icon>
                      {{ selectedLogo ? 'Logo Değiştir' : 'Logo Yükle' }}
                    </v-btn>
                    <span
                      v-if="selectedLogo"
                      class="mt-2 text-caption"
                    >
                      {{ selectedLogo.name }}
                    </span>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <div class="d-flex flex-column align-center">
                    <v-img
                      v-if="backgroundPreview || editedItem.background"
                      :src="backgroundPreview || editedItem.background"
                      max-width="200"
                      max-height="100"
                      contain
                      class="mb-4"
                    ></v-img>
                    <input
                      ref="backgroundInput"
                      type="file"
                      accept="image/*"
                      style="display: none"
                      @change="onBackgroundSelected"
                    />
                    <v-btn
                      color="primary"
                      outlined
                      :disabled="editSaveLoading"
                      @click="$refs.backgroundInput.click()"
                    >
                      <v-icon left>
                        {{ icons.mdiUpload }}
                      </v-icon>
                      {{ selectedBackground ? 'Arka planı değiştir' : 'Arka plan yükle' }}
                    </v-btn>
                    <span
                      v-if="selectedBackground"
                      class="mt-2 text-caption"
                    >
                      {{ selectedBackground.name }}
                    </span>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              text
              :disabled="editSaveLoading || aboutImageUploading"
              @click="closeEdit"
            >
              İptal
            </v-btn>
            <v-btn
              color="primary"
              :loading="editSaveLoading"
              :disabled="editLoadLoading || !!editLoadError || aboutImageUploading || (offerHasCascadeImpact && !offerImpactAcknowledged)"
              @click="saveEdit"
            >
              Kaydet
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="resultDialog"
        max-width="560"
        persistent
      >
        <v-card class="admin-dialog-card">
          <v-card-title>
            <v-icon
              left
              :color="dialogColor"
            >
              {{ dialogIcon }}
            </v-icon>
            {{ dialogTitle }}
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="text-h6 text-center">
              {{ dialogMessage }}
            </p>
            <div
              v-if="syncSummaryItems.length"
              class="sync-summary mt-6"
            >
              <div
                v-for="item in syncSummaryItems"
                :key="item.key"
                class="sync-summary__row"
              >
                <span class="sync-summary__label">{{ item.label }}</span>
                <strong class="sync-summary__value">{{ item.value }}</strong>
              </div>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="resultDialog = false"
            >
              Tamam
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiPencil, mdiClose, mdiUpload, mdiSync,
} from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import { isAuthError, resolveApiErrorMessage } from '@/utils/apiErrors'
import { editorContainer, imageHandler } from '@/utils/editor'

export default {
  name: 'Companies',
  components: {
    AdminPageHeader,
  },
  data() {
    return {
      activeTab: 0,
      companySources: [
        { id: 'ALL', sourceType: { displayName: 'Tümü', type: 'ALL' } },
      ],
      allCampaigns: [],
      campaigns: [],
      loading: false,
      tableError: '',
      searchValue: '',
      searchTimeout: null,
      syncStatusTimeout: null,
      pendingSyncStartedAt: null,
      selectedStatusFilter: 1,
      lastCatalogUpdateAt: null,
      syncStatus: null,
      headers: [
        {
          text: 'Eylemler', value: 'action', sortable: false, width: '88px',
        },
        {
          text: 'Logo', value: 'logo', sortable: false, width: '80px',
        },
        {
          text: 'Offer', value: 'name', sortable: true, width: '240px',
        },
        {
          text: 'Offer ID', value: 'offerId', sortable: true, width: '120px',
        },
        {
          text: 'Network', value: 'networkSource', sortable: true, width: '130px',
        },
        {
          text: 'Offer Durumu', value: 'offerStatus', sortable: true, width: '160px',
        },
        {
          text: 'Payout', value: 'payout', sortable: true, width: '120px',
        },
        {
          text: 'Son Güncelleme', value: 'modifiedAt', sortable: true, width: '160px',
        },
        {
          text: 'Homepage', value: 'homepageUrl', sortable: false, width: '260px',
        },
      ],
      params: {
        searchValue: '',
        page: 0,
        size: 10,
        sortBy: 'name',
        sortDirection: 'ASC',
      },
      options: {},
      totalElements: 0,
      requestOptions: {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      },
      fetchLoading: false,
      resultDialog: false,
      dialogMessage: '',
      dialogTitle: '',
      dialogColor: 'success',
      dialogIcon: 'mdi-check-circle',
      syncSummary: null,
      editDialog: false,
      editError: '',
      editSaveLoading: false,
      editLoadLoading: false,
      editLoadError: '',
      editRequestId: 0,
      aboutHtmlAvailable: false,
      initialAboutHtml: '',
      aboutImageUploading: false,
      editOriginal: {},
      offerImpactAcknowledged: false,
      aboutEditorOptions: {
        modules: {
          toolbar: {
            container: editorContainer,
            handlers: { image: () => this.uploadAboutImage() },
          },
        },
        placeholder: 'Marka bilgisi, kampanya koşulları ve trafik izinlerini ekleyin…',
        theme: 'snow',
      },
      editedItem: {
        id: 0,
        name: '',
        offerId: '',
        networkSource: '',
        active: 1,
        popular: false,
        homepageUrl: '',
        settlementHoldDays: 60,
        reconciliationWindowDays: 90,
        aboutHtml: '',
      },
      defaultItem: {
        id: 0,
        name: '',
        offerId: '',
        networkSource: '',
        active: 1,
        popular: false,
        homepageUrl: '',
        settlementHoldDays: 60,
        reconciliationWindowDays: 90,
        aboutHtml: '',
      },
      offerStatusOptions: [
        { text: 'Network izni yok', value: 0 },
        { text: 'Aktif', value: 1 },
        { text: 'Pasif', value: 2 },
        { text: 'Network tarafından kapatıldı', value: 3 },
      ],
      offerStatusFilterOptions: [
        { text: 'Tümü', value: 'all' },
        { text: 'Network izni yok', value: 0 },
        { text: 'Aktif', value: 1 },
        { text: 'Pasif', value: 2 },
        { text: 'Network tarafından kapatıldı', value: 3 },
        { text: 'Network deleted', value: 4 },
      ],
      icons: {
        mdiPencil,
        mdiClose,
        mdiUpload,
        mdiSync,
      },
      selectedLogo: null,
      logoPreview: null,
      selectedBackground: null,
      backgroundPreview: null,
    }
  },
  computed: {
    offerBecomingInvisible() {
      return this.resolveOfferStatusValue(this.editedItem.active) !== 1
        && this.editedItem.active !== this.editOriginal.active
    },
    offerHasCascadeImpact() {
      return this.offerBecomingInvisible || this.editedItem.popular !== this.editOriginal.popular
    },
    currentSourceId() {
      return this.companySources[this.activeTab]?.id
    },
    currentSourceLabel() {
      return this.companySources[this.activeTab]?.sourceType?.displayName || 'Tümü'
    },
    lastCatalogUpdateLabel() {
      if (!this.lastCatalogUpdateAt) {
        return ''
      }

      return this.formatDateTime(this.lastCatalogUpdateAt)
    },
    lastCatalogUpdateSourceLabel() {
      const source = String(this.syncStatus?.triggerSource || '').trim().toLowerCase()
      if (source === 'cron') return 'Cron'
      if (source === 'manual') return 'Manuel'

      return ''
    },
    syncSummaryItems() {
      if (!this.syncSummary) {
        return []
      }

      const summary = this.syncSummary

      return [
        { key: 'upstreamOffers', label: 'Networkten gelen offer', value: summary.upstreamOffers ?? 0 },
        { key: 'updated', label: 'Güncellenen offer', value: summary.updated ?? 0 },
        { key: 'created', label: 'Yeni offer', value: summary.created ?? 0 },
        { key: 'staleOffersMarkedDeleted', label: 'Network deleted işaretlenen', value: summary.staleOffersMarkedDeleted ?? summary.staleOffersMarkedClosed ?? 0 },
        { key: 'remainingMissingOfferRefs', label: 'Eksik offer ref', value: summary.remainingMissingOfferRefs ?? 0 },
      ]
    },
  },
  watch: {
    'editedItem.active': function resetStatusAcknowledgement() { this.offerImpactAcknowledged = false },
    'editedItem.popular': function resetPopularAcknowledgement() { this.offerImpactAcknowledged = false },
  },
  mounted() {
    this.loadCompanySources()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
    clearTimeout(this.syncStatusTimeout)
    this.editRequestId += 1
  },
  methods: {
    async loadCompanySources() {
      this.companySources = [
        { id: 'ALL', sourceType: { displayName: 'Tümü', type: 'ALL' } },
      ]
      this.selectedStatusFilter = 1
      await this.loadSyncStatus()
      await this.loadTableData()
    },
    async loadSyncStatus() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}offer/sync/status`,
          this.requestOptions,
        )
        const status = response.data.data || {}
        this.syncStatus = status
        if (status.finishedAt) {
          this.lastCatalogUpdateAt = status.finishedAt
        }
        if (status.summary) {
          this.syncSummary = status.summary
        }
      } catch (error) {
        console.warn('Offer sync state okunamadı, offer updated_at fallback kullanılacak:', error)
        this.syncStatus = null
      }
    },
    onTabChange() {
      this.searchValue = ''
      this.params.searchValue = ''
      this.params.page = 0
      this.campaigns = []
      this.totalElements = 0
      this.options = {}

      // Tab değiştiğinde verileri yükle
      this.$nextTick(() => {
        if (this.currentSourceId) {
          this.loadTableData()
        }
      })
    },
    onUpdateTableOptions(options) {
      if (!options || !options.page || !this.currentSourceId) return

      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = options

      this.params.page = page - 1
      this.params.size = itemsPerPage || 10

      if (sortBy && sortBy.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.params.sortBy = sortBy[0]
        this.params.sortDirection = sortDesc && sortDesc[0] ? 'DESC' : 'ASC'
      }

      this.loadTableData()
    },
    onSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.params.searchValue = this.searchValue
        this.params.page = 0
        this.loadTableData()
      }, 250)
    },
    onStatusFilterChange() {
      this.params.page = 0
      this.loadTableData()
    },
    async loadTableData() {
      this.loading = true
      this.tableError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}panel/offers`,
          {
            ...this.requestOptions,
            params: {
              page: 0,
              size: 5000,
              sort: `${this.resolveOfferSortField(this.params.sortBy)},${this.params.sortDirection.toLowerCase()}`,
            },
          },
        )

        if (response.data.status === 200 && response.data.data) {
          const page = response.data.data
          const list = page.content || []

          const normalized = list.map(item => ({
            ...item,
            logo: item.logo?.localUrl || item.logo?.url || '',
            background: item.background?.localUrl || item.background?.url || '',
            offerStatus: this.resolveOfferStatusValue(item.active),
            homepageUrl: item.homepageUrl || '-',
            active: this.resolveOfferStatusValue(item.active),
            popular: !!item.popular,
            modifiedAt: item.modified || item.created || null,
          }))

          const uniqueSources = [...new Set(normalized.map(item => item.networkSource).filter(Boolean))]
          this.companySources = [
            { id: 'ALL', sourceType: { displayName: 'Tümü', type: 'ALL' } },
            ...uniqueSources.map(source => ({
              id: source,
              sourceType: { displayName: source, type: source },
            })),
          ]

          const filteredBySource = this.currentSourceId && this.currentSourceId !== 'ALL'
            ? normalized.filter(item => item.networkSource === this.currentSourceId)
            : normalized

          if (!this.syncStatus?.finishedAt) {
            this.lastCatalogUpdateAt = this.resolveLatestOfferTimestamp(filteredBySource)
          }

          const filteredByStatus = this.selectedStatusFilter === 'all'
            ? filteredBySource
            : filteredBySource.filter(item => Number(item.offerStatus) === Number(this.selectedStatusFilter))

          const filtered = (this.params.searchValue || '').trim()
            ? filteredByStatus.filter(item => [item.name, item.offerId, item.networkSource]
              .filter(Boolean)
              .some(value => String(value).toLowerCase().includes(this.params.searchValue.toLowerCase())))
            : filteredByStatus

          this.allCampaigns = filtered
          this.totalElements = filtered.length

          const start = this.params.page * this.params.size
          const end = start + this.params.size
          this.campaigns = filtered.slice(start, end)
        }
      } catch (error) {
        console.error('Offer listesi yüklenirken hata oluştu:', error)
        if (isAuthError(error)) {
          this.$router.push({ name: 'login' })

          return
        }

        this.allCampaigns = []
        this.campaigns = []
        this.totalElements = 0
        this.tableError = resolveApiErrorMessage(error, 'Network offer listesi yüklenemedi.')
      } finally {
        this.loading = false
      }
    },
    resolveOfferStatusValue(active) {
      const normalized = Number(active)

      if ([0, 1, 2, 3, 4].includes(normalized)) {
        return normalized
      }

      return 1
    },
    resolveOfferStatusText(status) {
      switch (Number(status)) {
        case 0:
          return 'Network izni yok'
        case 2:
          return 'Pasif'
        case 3:
          return 'Network kapattı'
        case 4:
          return 'Network sildi'
        case 1:
        default:
          return 'Aktif'
      }
    },
    resolveOfferStatusColor(status) {
      switch (Number(status)) {
        case 0:
          return 'error'
        case 2:
          return 'warning'
        case 3:
          return 'secondary'
        case 4:
          return 'grey'
        case 1:
        default:
          return 'success'
      }
    },
    isPercentagePayout(item) {
      const payoutType = this.normalizePayoutType(item?.payoutType)

      return payoutType.includes('PERCENTAGE')
    },
    isFlatPayout(item) {
      const payoutType = this.normalizePayoutType(item?.payoutType)

      return payoutType.includes('FLAT')
    },
    normalizePayoutType(payoutType) {
      if (!payoutType) {
        return ''
      }

      return String(payoutType).trim().toUpperCase()
    },
    resolveLatestOfferTimestamp(items) {
      if (!Array.isArray(items) || !items.length) {
        return null
      }

      return items
        .map(item => item.modifiedAt || item.modified || item.created || null)
        .filter(Boolean)
        .map(value => this.resolveDateTime(value).getTime())
        .filter(value => !Number.isNaN(value))
        .reduce((latest, current) => (current > latest ? current : latest), 0) || null
    },
    resolveDateTime(timestamp) {
      if (!Array.isArray(timestamp)) {
        return new Date(timestamp)
      }

      const [year, month, day, hour = 0, minute = 0, second = 0, nanos = 0] = timestamp.map(Number)
      if (![year, month, day, hour, minute, second, nanos].every(Number.isFinite)) {
        return new Date(NaN)
      }

      return new Date(year, month - 1, day, hour, minute, second, Math.floor(nanos / 1000000))
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '-'

      const date = this.resolveDateTime(timestamp)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getOfferStatusText(status) {
      return this.resolveOfferStatusText(status)
    },
    getOfferStatusColor(status) {
      return this.resolveOfferStatusColor(status)
    },
    formatPayout(item) {
      if (item.payout === null || item.payout === undefined || item.payout === '') return '-'
      const normalizedPayout = Number(item.payout)
      const payoutText = Number.isNaN(normalizedPayout)
        ? String(item.payout)
        : new Intl.NumberFormat('tr-TR', {
          maximumFractionDigits: 2,
        }).format(normalizedPayout)

      if (this.isPercentagePayout(item)) {
        return `%${payoutText}`
      }

      if (this.isFlatPayout(item)) {
        const currency = String(item.currency || 'TRY').trim().toUpperCase()

        return `${payoutText} ${['TRY', 'TL'].includes(currency) ? 'TRY' : currency}`
      }

      return payoutText
    },
    resolveOfferSortField(sortBy) {
      const map = {
        offerId: 'externalId',
        networkSource: 'networkSource',
        offerStatus: 'active',
        payout: 'payout',
        modifiedAt: 'modified',
      }

      return map[sortBy] || sortBy || 'name'
    },
    async fetchCampaigns() {
      this.fetchLoading = true
      this.syncSummary = null
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}offer/sync`,
          null,
          this.requestOptions,
        )

        if (response.data.status === 200) {
          const status = response.data.data || {}
          this.pendingSyncStartedAt = status.startedAt || null
          this.fetchLoading = !!status.running
          if (status.finishedAt) {
            this.syncSummary = status.summary || null
            this.dialogTitle = status.success === false ? 'İşlem Başarısız' : 'İşlem Tamamlandı'
            this.dialogMessage = response.data.message || 'Network offer sync tamamlandı'
            this.dialogColor = status.success === false ? 'error' : 'success'
            this.dialogIcon = status.success === false ? 'mdi-alert-circle' : 'mdi-check-circle'
            this.resultDialog = true
            this.lastCatalogUpdateAt = status.finishedAt
            this.syncStatus = status
            await this.loadTableData()
          } else {
            this.pollSyncStatus()
          }
        }
      } catch (error) {
        console.error('Offer listesi çekilirken hata oluştu:', error)
        this.syncSummary = null
        this.dialogTitle = 'İşlem Başarısız'
        this.dialogMessage = 'Networklerden offer çekilirken hata oluştu'
        this.dialogColor = 'error'
        this.dialogIcon = 'mdi-alert-circle'
        this.resultDialog = true
      } finally {
        if (!this.pendingSyncStartedAt) {
          this.fetchLoading = false
        }
      }
    },
    async pollSyncStatus() {
      clearTimeout(this.syncStatusTimeout)
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}offer/sync/status`,
          this.requestOptions,
        )
        const status = response.data.data || {}
        this.fetchLoading = !!status.running

        if (status.finishedAt) {
          this.lastCatalogUpdateAt = status.finishedAt
          this.syncStatus = status
        }

        if (status.running) {
          this.syncStatusTimeout = setTimeout(() => {
            this.pollSyncStatus()
          }, 3000)

          return
        }

        if (this.pendingSyncStartedAt && status.startedAt === this.pendingSyncStartedAt) {
          this.syncSummary = status.summary || null
          this.dialogTitle = status.success === false ? 'İşlem Başarısız' : 'İşlem Tamamlandı'
          this.dialogMessage = response.data.message || 'Network offer sync tamamlandı'
          this.dialogColor = status.success === false ? 'error' : 'success'
          this.dialogIcon = status.success === false ? 'mdi-alert-circle' : 'mdi-check-circle'
          this.resultDialog = true
          await this.loadTableData()
        }
        this.pendingSyncStartedAt = null
      } catch (error) {
        console.error('Offer sync durumu okunamadı:', error)
        this.fetchLoading = false
        this.pendingSyncStartedAt = null
        this.syncSummary = null
        this.dialogTitle = 'İşlem Başarısız'
        this.dialogMessage = resolveApiErrorMessage(error, 'Network offer sync durumu okunamadı')
        this.dialogColor = 'error'
        this.dialogIcon = 'mdi-alert-circle'
        this.resultDialog = true
      }
    },
    normalizeEditedOffer(item) {
      return {
        ...this.defaultItem,
        ...item,
        logo: item.logo?.localUrl || item.logo || '',
        background: item.background?.localUrl || item.background || '',
        active: this.resolveOfferStatusValue(item.active),
        settlementHoldDays: Number(item.settlementHoldDays ?? 60),
        reconciliationWindowDays: Number(item.reconciliationWindowDays ?? 90),
        aboutHtml: typeof item.aboutHtml === 'string' ? item.aboutHtml : '',
      }
    },
    async editItem(item) {
      if (this.editSaveLoading || this.aboutImageUploading) return
      this.editRequestId += 1
      const requestId = this.editRequestId
      this.editedItem = this.normalizeEditedOffer(item)
      this.selectedLogo = null
      this.logoPreview = null
      this.selectedBackground = null
      this.backgroundPreview = null
      this.aboutHtmlAvailable = false
      this.initialAboutHtml = ''
      this.editError = ''
      this.editLoadError = ''
      this.editLoadLoading = true
      this.editDialog = true

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}panel/offers/${item.id}`,
          { headers: { Authorization: `Bearer ${getAccessToken()}` } },
        )
        if (requestId !== this.editRequestId || !this.editDialog) return
        const offer = response.data?.data
        if (response.data?.status !== 200 || !offer || String(offer.id) !== String(item.id)) {
          throw new Error(response.data?.message || 'Offer bilgileri alınamadı.')
        }
        this.editedItem = this.normalizeEditedOffer(offer)
        this.editOriginal = { ...this.editedItem }
        this.offerImpactAcknowledged = false
        this.aboutHtmlAvailable = Object.prototype.hasOwnProperty.call(offer, 'aboutHtml')
        this.initialAboutHtml = this.editedItem.aboutHtml
      } catch (error) {
        if (requestId !== this.editRequestId || !this.editDialog) return
        this.editLoadError = resolveApiErrorMessage(error, 'Offer bilgileri yüklenemedi. Lütfen tekrar deneyin.')
      } finally {
        if (requestId === this.editRequestId) this.editLoadLoading = false
      }
    },
    onEditDialogInput(open) {
      if (!open) this.closeEdit()
    },
    closeEdit() {
      if (this.editSaveLoading || this.aboutImageUploading) return
      this.editRequestId += 1
      this.editDialog = false
      this.selectedLogo = null
      this.logoPreview = null
      this.selectedBackground = null
      this.backgroundPreview = null
      this.editError = ''
      this.editLoadError = ''
      this.editLoadLoading = false
      this.editSaveLoading = false
      this.aboutHtmlAvailable = false
      this.initialAboutHtml = ''
      this.editedItem = { ...this.defaultItem }
      this.editOriginal = {}
      this.offerImpactAcknowledged = false
    },
    uploadAboutImage() {
      if (this.editSaveLoading || this.aboutImageUploading || !this.aboutHtmlAvailable) return
      const requestId = this.editRequestId
      const isCurrent = () => this.editDialog && requestId === this.editRequestId && !this.editSaveLoading
      imageHandler(this.$refs.offerAboutEditor, 'cdn.adverport.com/adverportaffiliate/offers/about-images', {
        isCurrent,
        onStart: () => {
          this.aboutImageUploading = true
          this.editError = ''
        },
        onError: error => {
          if (isCurrent()) this.editError = resolveApiErrorMessage(error, 'Marka Hakkında görseli yüklenemedi.')
        },
        onFinish: () => {
          if (requestId === this.editRequestId) this.aboutImageUploading = false
        },
      })
    },
    onLogoSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectedLogo = file
        const reader = new FileReader()
        reader.onload = e => {
          this.logoPreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    onBackgroundSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectedBackground = file
        const reader = new FileReader()
        reader.onload = e => {
          this.backgroundPreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    validateReconciliationPolicy() {
      const settlementHoldDays = Number(this.editedItem.settlementHoldDays)
      const reconciliationWindowDays = Number(this.editedItem.reconciliationWindowDays)
      const validDays = value => Number.isInteger(value) && value >= 1 && value <= 365

      if (!validDays(settlementHoldDays)) {
        return 'Kesinleşme bekleme süresi 1 ile 365 gün arasında tam sayı olmalı.'
      }
      if (!validDays(reconciliationWindowDays)) {
        return 'Mutabakat takip süresi 1 ile 365 gün arasında tam sayı olmalı.'
      }
      if (reconciliationWindowDays < settlementHoldDays) {
        return 'Mutabakat takip süresi kesinleşme bekleme süresinden kısa olamaz.'
      }

      return ''
    },
    async saveEdit() {
      if (!this.editDialog || this.editSaveLoading || this.editLoadLoading || this.editLoadError || this.aboutImageUploading) return
      if (this.offerHasCascadeImpact && !this.offerImpactAcknowledged) {
        this.editError = 'Bağlı kayıtlara etkisini onaylayın.'

        return
      }
      this.editError = this.validateReconciliationPolicy()
      if (this.editError) return

      this.editSaveLoading = true
      try {
        const formData = new FormData()
        formData.append('id', this.editedItem.id)
        formData.append('name', this.editedItem.name || '')
        formData.append('popular', String(!!this.editedItem.popular))
        formData.append('active', String(this.resolveOfferStatusValue(this.editedItem.active)))
        formData.append('settlementHoldDays', String(Number(this.editedItem.settlementHoldDays)))
        formData.append('reconciliationWindowDays', String(Number(this.editedItem.reconciliationWindowDays)))

        // Missing fields from an older backend must never clear existing content.
        if (this.aboutHtmlAvailable && this.editedItem.aboutHtml !== this.initialAboutHtml) {
          formData.append('aboutHtml', this.editedItem.aboutHtml || '')
        }
        if (this.selectedLogo) {
          formData.append('image', this.selectedLogo)
        }
        if (this.selectedBackground) {
          formData.append('background', this.selectedBackground)
        }

        const response = await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}offer/update`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        if (response.data?.status !== 200) {
          throw new Error(response.data?.message || 'Network offer güncellenemedi.')
        }

        await this.loadTableData()
        this.editSaveLoading = false
        this.closeEdit()
      } catch (error) {
        console.error('Network offer güncellenirken hata oluştu:', error)
        this.editError = resolveApiErrorMessage(error, 'Network offer güncellenemedi.')
      } finally {
        this.editSaveLoading = false
      }
    },
  },
}
</script>

<style scoped>
.offer-composer {
  display: grid;
  grid-template-columns: minmax(300px, 0.85fr) minmax(0, 1.15fr);
  gap: 12px 24px;
}
.offer-composer .row { margin: 0; }
.offer-composer .row > .col { padding: 6px; }
.offer-composer > .offer-about-section { grid-column: 2; grid-row: 1; align-content: start; }
.offer-composer > .v-alert { grid-column: 1 / -1; }
.offer-settings > .row { margin-bottom: 12px; }
@media (max-width: 800px) {
  .offer-composer { display: block; padding: 0; }
  .offer-composer > .row { margin-bottom: 12px; }
}
.offer-about-editor ::v-deep .ql-editor {
  min-height: 320px;
  max-height: 400px;
  overflow-y: auto;
}

.offer-about-editor ::v-deep .ql-tooltip {
  left: 0 !important;
  max-width: 100%;
  white-space: normal;
}

.search-field {
  max-width: 350px;
  min-width: 250px;
}

.status-filter {
  max-width: 220px;
  min-width: 180px;
}

.sync-fetch-chip {
  flex: 0 0 auto;
  max-width: none !important;
  white-space: nowrap;
}

.offer-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.offer-row span {
  font-size: 12px;
  opacity: 0.72;
}

.payout-cell {
  gap: 4px;
  white-space: nowrap;
}

.sync-summary {
  display: grid;
  gap: 10px;
  padding: 16px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.04);
}

.sync-summary__row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
}

.sync-summary__label {
  color: rgba(15, 23, 42, 0.72);
}

.sync-summary__value {
  font-size: 15px;
}
</style>
